import {
  allowTracking,
  fetchUserOnce,
  getCookie,
  getCustomerIOSiteId,
  getMainFbPixel,
  getTikTokPixel,
  hash,
  initAmplitudeAnalytics,
  initTrackerCustomerIO,
  initTrackerFacebook,
  initTrackerQuantcast,
  initTrackerTikTok,
  isServer,
  removeCookie,
  setMobile,
  unsetMobile,
  url,
} from '@/helpers';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';
import { modalActions, userActions } from './actions';
import { useConfidenceClient } from './hooks/useConfidenceExperiment';
import { AppDispatch, RootState } from './store';

const checkHash = (dispatch: AppDispatch) => hash.check(dispatch);
const initFacebookPixel = () => getMainFbPixel() && initTrackerFacebook(getMainFbPixel());
const initTikTokPixel = () => getTikTokPixel() && initTrackerTikTok(getTikTokPixel());
const initCustomerIO = () => {
  const siteId = getCustomerIOSiteId();
  if (!siteId) return;
  initTrackerCustomerIO(siteId);
};

export const initTracking = () => {
  if (!allowTracking()) return;
  sessionStorage.removeItem('disabledTrk');
  initFacebookPixel();
  initTikTokPixel();
  initCustomerIO();
  initTrackerQuantcast();
};

const initLastMinute = (dispatch: Dispatch<AnyAction>, history: any, location: any) => {
  const searchParams = new URLSearchParams(window.location.search);
  const fromLastMinute = Boolean(searchParams.get('lm'));
  const closedLastMinute = !isServer ? localStorage.getItem('closedLastMinute') === 'true' : undefined;
  const showLastMinuteModal = !closedLastMinute && fromLastMinute;
  dispatch(modalActions.lastMinute({ show: showLastMinuteModal }));

  if (fromLastMinute) {
    searchParams.delete('lm');
    history.replace({
      pathname: location.pathname,
      search: searchParams ? `?${searchParams}` : '',
    });
  }
};

const initNps = (dispatch: Dispatch<AnyAction>, location: any) => {
  const surveyId = getCookie('clientBooker');
  const npsClosed = getCookie('npsClosed');
  const blockedScreen =
    location.pathname.includes('/places/') ||
    location.pathname.includes('/boka-tjanst/') ||
    location.pathname.includes('checkout') ||
    location.pathname.includes('friskvardskort') ||
    location.pathname.includes('presentkort');
  const show = Boolean(surveyId) && !Boolean(npsClosed) && !blockedScreen;
  dispatch(modalActions.nps({ show }));
};

const addWindowListeners = (dispatch) => {
  window.addEventListener('pushstate', () => checkHash(dispatch), false);
  window.addEventListener('hashchange', () => checkHash(dispatch), false);

  return () => {
    window.removeEventListener('pushstate', () => checkHash(dispatch), false);
    window.removeEventListener('hashchange', () => checkHash(dispatch), false);
  };
};

const initCookies = (dispatch: AppDispatch, history, hasFetchedUserSSR: boolean) => {
  if (isServer) return;

  const replaceAutoLogin = () => history.replace({ search: url.changeGetParameter('auto-login') });
  const value = getCookie('LandingPage');
  if (value) {
    sessionStorage.setItem('LandingPage', value);
    removeCookie('LandingPage');
  }

  const token = getCookie('BokadirektMP') || getCookie('BokadirektOTT');

  if (token) {
    removeCookie('BokadirektMP');
    removeCookie('BokadirektOTT');
    // @ts-ignore
    dispatch(userActions.profile(token, true));
    setTimeout(replaceAutoLogin, 500);
    return;
  }

  if (window.location.hash === '#ZmV0Y2h1c2Vy') {
    // @ts-ignore
    dispatch(userActions.profile(null, null, true));
    window.location.hash = '';
    return;
  }

  // Skip client-side fetch if user was already loaded during SSR
  if (hasFetchedUserSSR) {
    return;
  }

  if (fetchUserOnce()) {
    // @ts-ignore
    dispatch(userActions.profile(null, null));
    return;
  }

  dispatch(userActions.setFetched());
};

const useOnce = (callback) => {
  const result = useRef(null);

  if (result.current !== null) {
    return result.current;
  }

  result.current = callback();
  return result.current;
};

const captureTrackingData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};

  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('utmParams', JSON.stringify(utmParams));
  }

  sessionStorage.setItem('originalReferrer', document.referrer || 'direct');
};

const useStartupEvents = (
  isMobile: boolean,
  bootstrapStyles: string[],
  setVisible: Dispatch<SetStateAction<boolean>>,
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const confidenceClient = useConfidenceClient();
  const hasFetchedUserSSR = useSelector((state: RootState) => state.users?.hasFetched ?? false);

  isMobile ? setMobile() : unsetMobile();

  /**
   * Handle defered css loading of not critical styles
   */
  useEffect(() => {
    const styleLinks = bootstrapStyles.map((stylePath) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = stylePath;
      link.media = 'print';

      link.onload = () => (link.media = 'all');
      return link;
    });

    styleLinks.forEach((link) => document.head.appendChild(link));

    return () => {
      styleLinks.forEach((link) => document.head.removeChild(link));
    };
  }, [bootstrapStyles]);

  useOnce(() => {
    checkHash(dispatch);
    initTracking();
    initCookies(dispatch, history, hasFetchedUserSSR);
    return true;
  });

  useEffect(
    () => initAmplitudeAnalytics(confidenceClient?.setDeviceId),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => initNps(dispatch, location), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => initLastMinute(dispatch, history, location), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => addWindowListeners(dispatch) && setVisible(true), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => captureTrackingData(), []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useStartupEvents;
